import React, { useEffect, useState, useContext, useRef } from 'react';
import { fetchWorkers } from '../api/workers';
import { fetchWorkflowsAndQueues } from '../api/workflowsAndQueues';
import TaskQueueFilter from './TaskQueueFilter';
import { MdSupportAgent, MdFreeBreakfast, MdOutlineLunchDining } from 'react-icons/md';
import { TbPhoneCalling } from 'react-icons/tb';
import { RiCustomerService2Fill, RiFullscreenFill, RiFullscreenExitLine } from 'react-icons/ri';
import { LuTextQuote } from 'react-icons/lu';
import { CgLogOff } from 'react-icons/cg';
import { BiFilterAlt } from 'react-icons/bi';
import LiveVoiceTasks from './LiveVoiceTasks';
import TeamFilterModal from './TeamFilterModal';
import LoadingIndicator from './shared/ui/LoadingIndicator';
import './WorkerDashboard.css';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../contexts/AuthContext';
import { useEnvironment } from '../utils/EnvironmentContext';

/* =====================================
   Activity Categories
   ===================================== */
const CATEGORIES = [
  {
    key: 'ready',
    label: 'Ready',
    match: (name) => name.trim().toLowerCase() === 'ready',
    icon: MdSupportAgent,
    color: '#10B981',
  },
  {
    key: 'handling',
    label: 'Handling Call Session',
    match: (name) => name.trim().toLowerCase() === 'handling call session',
    icon: TbPhoneCalling,
    color: '#EF4444',
  },
  {
    key: 'onCall',
    label: 'On a Call',
    match: (name) => {
      const n = name.trim().toLowerCase();
      return n === 'on a call' || n === 'handling call session';
    },
    icon: RiCustomerService2Fill,
    color: '#8B5CF6',
  },
  {
    key: 'notReadyShortBreak',
    label: 'Not Ready - Short Break',
    match: (name) => name.toLowerCase().includes('not ready') && name.toLowerCase().includes('short break'),
    icon: MdFreeBreakfast,
    color: '#6B7280',
  },
  {
    key: 'notReadyLunchBreak',
    label: 'Not Ready - Lunch Break',
    match: (name) => name.toLowerCase().includes('not ready') && name.toLowerCase().includes('lunch'),
    icon: MdOutlineLunchDining,
    color: '#6B7280',
  },
  {
    key: 'admin',
    label: 'Admin Internal - Customer Feedback',
    match: (name) => name.toLowerCase().includes('admin internal') && name.toLowerCase().includes('customer feedback'),
    icon: LuTextQuote,
    color: '#EF4444',
  },
  {
    key: 'logoff',
    label: 'Logoff',
    match: (name) => name?.toLowerCase().includes('logoff'),
    icon: CgLogOff,
    color: '#6B7280',
  },
];

// Totals strip categories (desktop)
const overviewCategories = ['ready','onCall','admin','notReadyShortBreak','notReadyLunchBreak','logoff'];
// const mobileCategories   = ['ready','onCall','handling','admin','notReadyShortBreak','notReadyLunchBreak','logoff']; // unused

/* =====================================
   Helper
   ===================================== */
const deriveTeams = (workers) => {
  const set = new Set();
  workers.forEach(w => {
    const raw = w?.attributes?.team_name ?? w?.attributes?.team ?? w?.attributes?.teamName ?? '';
    const name = (typeof raw === 'string' ? raw : '').trim();
    if (name) set.add(name);
  });
  return Array.from(set).sort((a,b)=>a.localeCompare(b));
};

const WorkerDashboard = () => {
  const { userId } = useContext(AuthContext);
  const { env } = useEnvironment();

  // Data state
  const [workers, setWorkers] = useState([]);
  const [workersLoading, setWorkersLoading] = useState(true);
  const [taskQueues, setTaskQueues] = useState([]);
  const [queuesLoading, setQueuesLoading] = useState(true);
  const [selectedTaskQueues, setSelectedTaskQueues] = useState([]);
  // Show full-screen loader only on first load
  const [initializing, setInitializing] = useState(true);

  // UI / filters
  const [visibleTeams, setVisibleTeams] = useState(new Set()); // empty => show all
  const [filterOpen, setFilterOpen] = useState(false);
  const [showTaskQueueFilter, setShowTaskQueueFilter] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null); // for desktop team popup & mobile team detail
  const [maximized, setMaximized] = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false); // aggregated overlay

  // Layout / refs
  const bottomBarRef = useRef(null);
  const rootRef = useRef(null);
  const [bottomPadding, setBottomPadding] = useState(0);

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches;
  const iconSize = isMobile ? 24 : 42;

  // Toggle a body class so global UI (like navbar) can respond in fullscreen
  useEffect(() => {
    if (maximized) {
      document.body.classList.add('dashboard-fullscreen');
    } else {
      document.body.classList.remove('dashboard-fullscreen');
    }
    return () => { document.body.classList.remove('dashboard-fullscreen'); };
  }, [maximized]);

  /* ---------------- Workers ---------------- */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setWorkersLoading(true);
        const data = await fetchWorkers(env);
        const parsed = (Array.isArray(data) ? data : []).map(w => ({
          ...w,
          attributes: typeof w.attributes === 'string' ? (() => { try { return JSON.parse(w.attributes); } catch { return {}; } })() : w.attributes
        }));
        if(!cancelled) setWorkers(parsed);
      } catch(e){
        console.error('Failed to fetch workers', e);
      } finally {
        if(!cancelled) setWorkersLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(interval); };
  }, [env]);


  /* -------- Workflows & Queues (names only) -------- */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setQueuesLoading(true);
        const data = await fetchWorkflowsAndQueues(env);
        if (!cancelled) {
          const source = Array.isArray(data?.taskQueues)
            ? data.taskQueues
            : Array.isArray(data?.queues)
              ? data.queues
              : [];
          const normalized = source.map(q => {
            if (typeof q === 'string') return { sid: q, friendlyName: q };
            const sid = q?.sid || null;
            const friendlyName = q?.friendlyName || q?.friendly_name || sid || String(q);
            return { sid, friendlyName };
          });
          setTaskQueues(normalized);
        }
      } catch(e){
        console.error('Failed to fetch queues', e);
      } finally { if(!cancelled) setQueuesLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [env]);

  // After first successful load of both workers and queues, stop showing the initial overlay
  useEffect(() => {
    if (initializing && !workersLoading && !queuesLoading) {
      setInitializing(false);
    }
  }, [initializing, workersLoading, queuesLoading]);

  /* -------- Saved Taskqueues (one-time load) -------- */
  useEffect(() => {
    if(!userId) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db,'users',userId,'savedTaskqueues'));
        const arr = [];
        snap.forEach(d => arr.push(d.id));
        setSelectedTaskQueues(arr);
      } catch(e){
        console.error('Failed loading saved taskqueues', e);
      }
    })();
  }, [userId]);

  /* -------- Saved Teams (realtime) -------- */
  useEffect(() => {
    if(!userId) return;
    const colRef = collection(db,'users',userId,'savedTeams');
    const unsub = onSnapshot(colRef, snap => {
      const next = new Set();
      snap.forEach(doc => next.add(doc.id));
      setVisibleTeams(next); // empty set means show all
    }, err => console.error('savedTeams snapshot error', err));
    return () => unsub();
  }, [userId]);

  /* -------- Bottom padding (mobile bar height) -------- */
  useEffect(() => {
    if(!isMobile) return;
    const el = bottomBarRef.current;
    if(!el) return;
    const resizeObserver = new ResizeObserver(() => {
      setBottomPadding(el.getBoundingClientRect().height);
    });
    resizeObserver.observe(el);
    setBottomPadding(el.getBoundingClientRect().height);
    return () => resizeObserver.disconnect();
  }, [isMobile]);

  /* -------- Derived -------- */
  const teams = deriveTeams(workers);
  // Apply only filters that match current teams; if none match, show all
  const activeFilter = Array.from(visibleTeams).filter(t => teams.includes(t));
  // Exclude the catch-all team "Anderen" from sidebars
  const excludedFromSidebars = (name) => (name || '').trim().toLowerCase() === 'anderen';
  const filteredTeamsAll = activeFilter.length === 0 ? teams : teams.filter(t => activeFilter.includes(t));
  const filteredTeams = filteredTeamsAll.filter(t => !excludedFromSidebars(t));
  const midpoint = Math.ceil(filteredTeams.length / 2);
  const leftTeams = filteredTeams.slice(0, midpoint);
  const rightTeams = filteredTeams.slice(midpoint);
  const normalizedVisibleTeams = activeFilter.length === 0 ? new Set() : new Set(activeFilter);

  const countWorkers = (team, matchFn) => {
    return workers.filter(w => {
      const raw = w?.attributes?.team_name ?? w?.attributes?.team ?? w?.attributes?.teamName ?? '';
      const teamName = (typeof raw === 'string' ? raw : '').trim();
      if(team && team !== 'ALL' && teamName !== team) return false;
      if(activeFilter.length > 0 && !activeFilter.includes(teamName)) return false;
      return matchFn(w.activityName || '');
    }).length;
  };

  const groupedWorkers = () => {
    // For mobile detail overlay – all (filtered) workers sorted by category, team, name
    const withCat = workers
      .filter(w => {
        const raw = w?.attributes?.team_name ?? w?.attributes?.team ?? w?.attributes?.teamName ?? '';
        const teamName = (typeof raw === 'string' ? raw : '').trim();
        return activeFilter.length === 0 || activeFilter.includes(teamName);
      })
      .map(w => {
        const cat = CATEGORIES.find(c => c.match(w.activityName||''));
        return { ...w, __catIndex: cat ? CATEGORIES.indexOf(cat) : 999, __catColor: cat?.color, __catLabel: cat?.label };
      })
      .sort((a,b) => {
        if(a.__catIndex !== b.__catIndex) return a.__catIndex - b.__catIndex;
        const ta = (a.attributes?.team_name || '').localeCompare(b.attributes?.team_name || '');
        if(ta !== 0) return ta;
        return (a.friendlyName || '').localeCompare(b.friendlyName || '');
      });
    return withCat;
  };

  const toggleTeamVisibility = (team) => {
    setVisibleTeams(prev => {
      const next = new Set(prev);
      if(next.has(team)) next.delete(team); else next.add(team);
      return next;
    });
  };

  /* =====================================
     RENDER
     ===================================== */
  return (
    <div ref={rootRef} className={"worker-dashboard" + (maximized ? ' worker-dashboard--max' : '')} style={isMobile ? { paddingBottom: bottomPadding + 'px' } : undefined}>
      {/* Top global loading overlay */}
      {initializing && (workersLoading || queuesLoading) && (
        <div className="worker-dashboard__initialLoader" aria-label="Data wordt geladen">
          <div className="worker-dashboard__initialLoaderInner">
            <LoadingIndicator label={null} size={48} inline={false} />
          </div>
        </div>
      )}

      {/* Maximize toggle (desktop) */}
      {!isMobile && (
        <button
          onClick={() => setMaximized(m=>!m)}
          className="worker-dashboard__maximizeBtn"
          title={maximized ? 'Sluit volledig scherm' : 'Volledig scherm'}
        >
          {maximized ? <RiFullscreenExitLine /> : <RiFullscreenFill />}
        </button>
      )}

      {/* Filter Modal */}
      {filterOpen && (
        <TeamFilterModal
          teams={teams}
          visibleTeams={visibleTeams}
            toggleTeamVisibility={toggleTeamVisibility}
          onClose={() => setFilterOpen(false)}
          userId={userId}
        />
      )}

      {/* Taskqueue filter modal */}
      {showTaskQueueFilter && (
        <div className="wd-modalOverlay" onClick={() => setShowTaskQueueFilter(false)} style={isMobile ? { padding: 0 } : undefined}>
          <div
            className="wd-modalContent"
            onClick={e=>e.stopPropagation()}
            style={isMobile ? { width:'100vw', height:'100vh', maxHeight:'100vh', borderRadius:0, margin:0, padding:'1rem' } : undefined}
          >
            <TaskQueueFilter
              availableQueues={taskQueues}
              onChange={setSelectedTaskQueues}
              selectedTaskqueues={selectedTaskQueues}
              userId={userId}
              onClose={() => setShowTaskQueueFilter(false)}
            />
          </div>
        </div>
      )}

      {/* Team member modal (desktop) */}
      {selectedTeam && !isMobile && (
        <div className="wd-modalOverlay" onClick={() => setSelectedTeam(null)}>
          <div className="wd-modalContent" onClick={e=>e.stopPropagation()}>
            <h2 style={{ marginBottom:'1rem' }}>{selectedTeam === 'ALL' ? 'Alle teams – Medewerkers' : `${selectedTeam} Team Members`}</h2>
            <ul className="wd-teamList">
              {(
                selectedTeam === 'ALL'
                  ? [...workers]
                      .sort((a,b)=> {
                        const ta = ((a.attributes?.team_name||a.attributes?.team||a.attributes?.teamName||'')+ '').localeCompare((b.attributes?.team_name||b.attributes?.team||b.attributes?.teamName||'')+ '');
                        if (ta !== 0) return ta;
                        return (a.friendlyName||'').localeCompare(b.friendlyName||'');
                      })
                  : workers
                      .filter(w => {
                        const raw = w?.attributes?.team_name ?? w?.attributes?.team ?? w?.attributes?.teamName ?? '';
                        const teamName = (typeof raw === 'string' ? raw : '').trim();
                        return teamName === selectedTeam;
                      })
                      .sort((a,b)=> (a.friendlyName||'').localeCompare(b.friendlyName||''))
              ).map(w => {
                const raw = w?.attributes?.team_name ?? w?.attributes?.team ?? w?.attributes?.teamName ?? '';
                const teamName = (typeof raw === 'string' ? raw : '').trim();
                const cat = CATEGORIES.find(c => c.match(w.activityName||''));
                return (
                  <li key={w.sid} style={{ display:'flex', gap:'1rem', borderBottom:'1px solid #e5e7eb', padding:'.4rem 0', fontSize:'.8rem' }}>
                    <span style={{ flex:2 }}>{w.friendlyName}</span>
                    {selectedTeam === 'ALL' && (
                      <span style={{ flex:1, color:'#057fa2' }}>{teamName || 'Anderen'}</span>
                    )}
                    <span style={{ flex:1, color:cat?.color }}>{w.activityName}</span>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Columns wrapper */}
      <div className="worker-dashboard__pageContainer" style={maximized ? { margin:'2rem auto', maxWidth:'calc(100vw - 4rem)' } : undefined}>
        <div className="worker-dashboard__columns">
          {/* Left teams column (desktop) */}
          {!isMobile && (
            <div className="wd-teamsColumn">
              {leftTeams.map(team => (
                <div key={team} className="wd-teamPanel" onClick={()=>setSelectedTeam(team)}>
                  <h2>{team.replace(/memo|health/gi,'').trim()}</h2>
                  <div className="wd-teamPanelStats">
                    {CATEGORIES.filter(c => !['notReadyShortBreak','notReadyLunchBreak','handling'].includes(c.key))
                      .map(cat => {
                        return (
                          <div key={cat.key} className="wd-teamPanelStat">
                            <cat.icon size={iconSize} color={cat.color} />
                            <span style={{ color:cat.color }}>
                              {countWorkers(team, cat.match)}
                            </span>
                          </div>
                        );
                      })}
                    <div className="wd-breakGroup">
                      <div className="wd-breakItem">
                        <MdFreeBreakfast size={iconSize - (isMobile?4:12)} color={CATEGORIES.find(c=>c.key==='notReadyShortBreak').color} />
                        <span>{countWorkers(team, CATEGORIES.find(c=>c.key==='notReadyShortBreak').match)}</span>
                      </div>
                      <div className="wd-breakItem">
                        <MdOutlineLunchDining size={iconSize - (isMobile?4:12)} color={CATEGORIES.find(c=>c.key==='notReadyLunchBreak').color} />
                        <span>{countWorkers(team, CATEGORIES.find(c=>c.key==='notReadyLunchBreak').match)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Middle column (voice tasks + totals bar) */}
          <div className="wd-middleColumn">
            {!isMobile && (
              <div className="worker-dashboard__totalsBar">
                <div className="wd-totals" onClick={()=>setSelectedTeam('ALL')} title="Alle medewerkers">
                  {(() => {
                    const mid = Math.ceil(overviewCategories.length / 2);
                    const left = overviewCategories.slice(0, mid);
                    const right = overviewCategories.slice(mid);
                    return (
                      <>
                        {left.map(key => {
                          const cat = CATEGORIES.find(c=>c.key===key);
                          const count = countWorkers(null, cat.match);
                          return (
                            <div key={key} className="wd-totalItem">
                              <cat.icon size={24} color={cat.color} />
                              <span style={{ color:cat.color }}>{count}</span>
                            </div>
                          );
                        })}
                        <div key="env-label" style={{ display:'flex', alignItems:'center', padding:0, background:'transparent', border:'none' }}>
                          <span style={{ color:'#057fa2', fontWeight:700 }}>{env?.toUpperCase?.() || String(env)}</span>
                        </div>
                        {right.map(key => {
                          const cat = CATEGORIES.find(c=>c.key===key);
                          const count = countWorkers(null, cat.match);
                          return (
                            <div key={key} className="wd-totalItem">
                              <cat.icon size={24} color={cat.color} />
                              <span style={{ color:cat.color }}>{count}</span>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
                <div className="wd-totalActions">
                  <button onClick={()=>setShowTaskQueueFilter(true)} className="worker-dashboard__filterBtn" aria-label="Open queue filter"><BiFilterAlt /> Queuefilter</button>
                  <button onClick={()=>setFilterOpen(true)} className="worker-dashboard__filterBtn" aria-label="Open team filter"><BiFilterAlt /> Teamfilter</button>
                </div>
              </div>
            )}
            {isMobile && (
              <div className="worker-dashboard__filterButtonsRow">
                <button onClick={()=>setFilterOpen(true)} className="worker-dashboard__filterBtn" aria-label="Open team filter"><BiFilterAlt /> Teams</button>
                <button onClick={()=>setShowTaskQueueFilter(true)} className="worker-dashboard__filterBtn" aria-label="Open queue filter"><BiFilterAlt /> Queues</button>
              </div>
            )}
            <div className="wd-voiceTasksWrapper">
              <LiveVoiceTasks env={env} visibleTeams={normalizedVisibleTeams} maximized={maximized} selectedTaskQueues={selectedTaskQueues} />
            </div>
          </div>

          {/* Right teams column (desktop) */}
          {!isMobile && rightTeams.length > 0 && (
            <div className="wd-teamsColumn">
              {rightTeams.map(team => (
                <div key={team} className="wd-teamPanel" onClick={()=>setSelectedTeam(team)}>
                  <h2>{team.replace(/memo|health/gi,'').trim()}</h2>
                  <div className="wd-teamPanelStats">
                    {CATEGORIES.filter(c => !['notReadyShortBreak','notReadyLunchBreak','handling'].includes(c.key))
                      .map(cat => (
                        <div key={cat.key} className="wd-teamPanelStat">
                          <cat.icon size={iconSize} color={cat.color} />
                          <span style={{ color:cat.color }}>{countWorkers(team, cat.match)}</span>
                        </div>
                      ))}
                    <div className="wd-breakGroup">
                      <div className="wd-breakItem">
                        <MdFreeBreakfast size={iconSize - (isMobile?4:12)} color={CATEGORIES.find(c=>c.key==='notReadyShortBreak').color} />
                        <span>{countWorkers(team, CATEGORIES.find(c=>c.key==='notReadyShortBreak').match)}</span>
                      </div>
                      <div className="wd-breakItem">
                        <MdOutlineLunchDining size={iconSize - (isMobile?4:12)} color={CATEGORIES.find(c=>c.key==='notReadyLunchBreak').color} />
                        <span>{countWorkers(team, CATEGORIES.find(c=>c.key==='notReadyLunchBreak').match)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile bottom teams panels (fixed) */}
      {isMobile && (
        <div ref={bottomBarRef} className="worker-dashboard__bottomTeamsBar">
          <div className="bottomTeamsBar__rows">
            {filteredTeams.length > 0 ? (
              filteredTeams.map(team => {
                const leftKeys = ['ready','onCall','handling'];
                const rightKeys = ['notReadyShortBreak','notReadyLunchBreak','logoff'];
                return (
                  <div key={team} className="bottomTeamsRow" title={team} onClick={()=>{ setSelectedTeam(team); if(isMobile) setMobileDetailOpen(true); }}>
                    <div className="rowIcons rowIcons--left">
                      {leftKeys.map(key => {
                        const cat = CATEGORIES.find(c=>c.key===key);
                        if(!cat) return null;
                        const Icon = cat.icon;
                        const count = countWorkers(team, cat.match);
                        return (
                          <div key={key} className="rowStat">
                            <Icon size={14} color={cat.color} />
                            <span style={{ color:cat.color }}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="rowTeamName">{team.replace(/memo|health/gi,'').trim()}</div>
                    <div className="rowIcons rowIcons--right">
                      {rightKeys.map(key => {
                        const cat = CATEGORIES.find(c=>c.key===key);
                        if(!cat) return null;
                        const Icon = cat.icon;
                        const count = countWorkers(team, cat.match);
                        return (
                          <div key={key} className="rowStat">
                            <Icon size={14} color={cat.color} />
                            <span style={{ color:cat.color }}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bottomTeamsBar__empty">Geen teams geselecteerd</div>
            )}
          </div>
        </div>
      )}

      {/* Mobile detailed overlay */}
      {isMobile && mobileDetailOpen && (
        <div className="wd-mobileDetailOverlay" onClick={()=>setMobileDetailOpen(false)}>
          <div className="wd-mobileDetailPanel" onClick={e=>e.stopPropagation()}>
            <div className="wd-mobileDetailHeader">
              <h3>Status detail {selectedTeam ? `— ${selectedTeam.replace(/memo|health/gi,'').trim()}` : ''}</h3>
              <button onClick={()=>setMobileDetailOpen(false)}>Sluiten</button>
            </div>
            <div className="wd-mobileWorkersList">
              {(selectedTeam && selectedTeam !== 'ALL' 
                ? groupedWorkers().filter(w => {
                    const raw = w?.attributes?.team_name ?? w?.attributes?.team ?? w?.attributes?.teamName ?? '';
                    const name = (typeof raw === 'string' ? raw : '').trim();
                    return name === selectedTeam;
                  })
                : groupedWorkers()
              ).map(w => (
                <div key={w.sid} className="wd-mobileWorkerRow" style={{ borderColor: (w.__catColor||'#e5e7eb') }}>
                  <span className="name">{w.friendlyName}</span>
                  <span className="team">{(w.attributes?.team_name||'').replace(/memo|health/gi,'').trim()}</span>
                  <span className="activity" style={{ color:w.__catColor }}>{w.activityName}</span>
                </div>
              ))}
              {(selectedTeam && selectedTeam !== 'ALL' 
                ? groupedWorkers().filter(w => {
                    const raw = w?.attributes?.team_name ?? w?.attributes?.team ?? w?.attributes?.teamName ?? '';
                    const name = (typeof raw === 'string' ? raw : '').trim();
                    return name === selectedTeam;
                  }).length === 0
                : groupedWorkers().length === 0
              ) && (
                <div className="wd-empty">Geen medewerkers</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerDashboard;

